import { useEffect, useRef } from 'react';
import { AreaSeries, ColorType, createChart, type IChartApi, type ISeriesApi, type UTCTimestamp } from 'lightweight-charts';
import type { EquityPoint } from '@platform/shared';
import { cssVar, useTheme } from '../../lib/theme';

const toTime = (day: string) => (Date.parse(`${day}T00:00:00Z`) / 1000) as UTCTimestamp;

/** Area chart of daily equity. `points` oldest first; the last one may be today's live value. */
export function EquityChart({ points, up }: { points: EquityPoint[]; up: boolean }) {
  const box = useRef<HTMLDivElement>(null);
  const chart = useRef<IChartApi | null>(null);
  const series = useRef<ISeriesApi<'Area'> | null>(null);
  const theme = useTheme();

  useEffect(() => {
    if (!box.current) return;
    const c = createChart(box.current, {
      autoSize: true,
      handleScale: false,
      handleScroll: false,
      rightPriceScale: { borderVisible: false, scaleMargins: { top: 0.12, bottom: 0.06 } },
      timeScale: { borderVisible: false, fixLeftEdge: true, fixRightEdge: true },
      localization: { priceFormatter: (p: number) => p.toLocaleString('en-US', { maximumFractionDigits: p >= 1000 ? 0 : 2 }) },
    });
    series.current = c.addSeries(AreaSeries, { lineWidth: 2, priceLineVisible: false, lastValueVisible: true });
    chart.current = c;
    return () => {
      c.remove();
      chart.current = null;
      series.current = null;
    };
  }, []);

  useEffect(() => {
    chart.current?.applyOptions({
      layout: { background: { type: ColorType.Solid, color: 'transparent' }, textColor: cssVar('--muted'), fontFamily: 'Geist Mono, monospace', fontSize: 12 },
      grid: { vertLines: { visible: false }, horzLines: { color: cssVar('--grid') } },
      crosshair: { vertLine: { color: cssVar('--border-strong') }, horzLine: { color: cssVar('--border-strong') } },
    });
    series.current?.applyOptions({
      lineColor: cssVar(up ? '--green' : '--red'),
      topColor: cssVar(up ? '--green-bg' : '--red-bg'),
      bottomColor: 'rgba(0,0,0,0)',
    });
  }, [theme, up]);

  useEffect(() => {
    if (!series.current) return;
    series.current.setData(points.map((p) => ({ time: toTime(p.day), value: Number(p.equity) })));
    chart.current?.timeScale().fitContent();
  }, [points]);

  return <div ref={box} className="h-[260px] w-full md:h-[300px]" />;
}
